/**
 * SettingsPage — per-user settings (NC-505 Phase 5 PR-C).
 *
 * Phase 5.0 ships one section: Benachrichtigungen (Teams webhook URL +
 * image-build master toggle). The page is user-scoped (viewer+), every
 * user edits only their own UserSettingsDoc.
 *
 * Notifications stay server-side gated behind NOTIFICATIONS_TEAMS_ENABLED
 * (operator flag, default off) — the webhook test response carries the flag
 * state so the page can surface that real notifications are still off.
 */

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Snackbar,
  Switch,
  Typography,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { userSettingsApi } from '../services/userSettingsService';
import { UserSettingsDoc } from '../services/types';
import { useApiQuery } from '../hooks/useApiQuery';
import ErrorMessage from '../components/ErrorMessage';
import TeamsWebhookField, {
  isAllowedWebhookUrl,
} from '../components/notifications/TeamsWebhookField';

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

export default function SettingsPage() {
  const { data: settings, loading, error, refetch } = useApiQuery<UserSettingsDoc>(
    () => userSettingsApi.get(),
    []
  );

  const [webhookUrl, setWebhookUrl] = useState('');
  const [imageBuild, setImageBuild] = useState(false);
  const [baseline, setBaseline] = useState<UserSettingsDoc | null>(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [teamsFlagOff, setTeamsFlagOff] = useState(false);
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'info',
  });

  // Seed the form once the doc arrives (and after refetch).
  useEffect(() => {
    if (settings) {
      setWebhookUrl(settings.teamsWebhookUrl ?? '');
      setImageBuild(settings.notifications.imageBuild);
      setBaseline(settings);
    }
  }, [settings]);

  const showSnackbar = (message: string, severity: SnackbarState['severity']) => {
    setSnackbar({ open: true, message, severity });
  };

  const trimmedUrl = webhookUrl.trim();
  const urlDirty = (baseline?.teamsWebhookUrl ?? '') !== trimmedUrl;
  const toggleDirty = (baseline?.notifications.imageBuild ?? false) !== imageBuild;
  const dirty = urlDirty || toggleDirty;
  const urlInvalid = trimmedUrl !== '' && !isAllowedWebhookUrl(trimmedUrl);

  const handleSave = async () => {
    if (urlInvalid) return;
    setSaving(true);
    try {
      const saved = await userSettingsApi.update({
        // null clears the stored URL; a non-empty string sets it.
        teamsWebhookUrl: trimmedUrl === '' ? null : trimmedUrl,
        notifications: { imageBuild },
      });
      setBaseline(saved);
      setWebhookUrl(saved.teamsWebhookUrl ?? '');
      setImageBuild(saved.notifications.imageBuild);
      showSnackbar('Einstellungen gespeichert', 'success');
    } catch (err) {
      showSnackbar(
        `Fehler beim Speichern: ${err instanceof Error ? err.message : 'unbekannt'}`,
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const result = await userSettingsApi.testWebhook();
      setTeamsFlagOff(!result.teamsEnabled);
      showSnackbar(
        result.message || `Test-Karte gesendet (HTTP ${result.httpStatus})`,
        'success'
      );
    } catch (err) {
      showSnackbar(
        `Webhook-Test fehlgeschlagen: ${err instanceof Error ? err.message : 'unbekannt'}`,
        'error'
      );
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !settings) {
    return (
      <ErrorMessage
        message={error || 'Einstellungen nicht gefunden'}
        title="Einstellungen konnten nicht geladen werden"
        onRetry={refetch}
      />
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Einstellungen
      </Typography>

      <Card sx={{ maxWidth: 720 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Benachrichtigungen
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Bei Abschluss eines Image-Builds (erfolgreich oder fehlgeschlagen) wird eine
            Karte an deinen Microsoft-Teams-Channel gesendet.
          </Typography>

          {teamsFlagOff && (
            <Alert severity="info" sx={{ mb: 2 }} onClose={() => setTeamsFlagOff(false)}>
              Test erfolgreich — serverseitige Benachrichtigungen sind aber noch deaktiviert
              (Operator-Flag NOTIFICATIONS_TEAMS_ENABLED). Deine Konfiguration wird aktiv,
              sobald das Flag gesetzt ist.
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TeamsWebhookField
              value={webhookUrl}
              onChange={setWebhookUrl}
              onTest={handleTest}
              testing={testing}
              dirty={urlDirty}
              savedUrlPresent={Boolean(baseline?.teamsWebhookUrl)}
              disabled={saving}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={imageBuild}
                  onChange={(e) => setImageBuild(e.target.checked)}
                  disabled={saving}
                />
              }
              label="Bei Image-Build-Abschluss benachrichtigen"
            />

            <Box>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving || !dirty || urlInvalid}
                startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
              >
                {saving ? 'Speichere…' : 'Speichern'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
